import { useAppUserContext } from 'app/useAppUserContext'
import { useAgents } from 'data/hooks/agents/agents'
import { Agent } from 'data/hooks/agents/types'
import { useNavigation } from 'data/hooks/navigation'
import { useObjects } from 'data/hooks/objects'
import { usePages } from 'data/hooks/pages'
import { useViews } from 'data/hooks/views'
import { useWorkflows } from 'data/hooks/workflows/workflows'
import { getFieldIcon } from 'features/admin/fields/icons/utils'
import { buildNavTree } from 'features/admin/settings/navigation/NavigationUtils'
import { sortFieldsByOrder } from 'features/fields/utils'
import {
    mapNavTreeToAppNavigationMenuItems,
    mapStackToNavigationApp,
} from 'features/navigation/utils'
import {
    AppTemplateContentItem,
    AppTemplateContentSection,
    AppTemplateIcon,
} from 'features/templates/types'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { IconValue } from 'ui/components/IconPicker/IconPickerDropdown'

function getIconFromIconValue(icon?: IconValue): AppTemplateIcon | undefined {
    if (!icon) return undefined

    return {
        family: icon.type,
        name: icon.name,
    }
}

export function useGetAppTemplateFromStack(stack: StackDto) {
    const navigationApp = mapStackToNavigationApp({ stack })

    const { data: stackObjects = [], isLoading: isLoadingObjects } = useObjects({
        options: {
            stackId: stack._sid,
        },
    })

    const { role } = useAppUserContext()

    const { data: stackNavigation = [], isLoading: isLoadingNavigation } = useNavigation({
        stackId: stack._sid,
    })
    const { data: stackViews = [], isLoading: isLoadingViews } = useViews({ stackId: stack._sid })
    const { data: stackPages = [], isLoading: isLoadingPages } = usePages({ stackId: stack._sid })

    const { data: stackAgents = [], isLoading: isLoadingAgents } = useAgents({
        stackId: stack._sid,
    })
    const { data: stackWorkflows = [], isLoading: isLoadingWorkflows } = useWorkflows({
        stackId: stack._sid,
    })

    const contents = getTemplateContentSections(
        stack,
        role!,
        stackObjects,
        stackNavigation as NavigationDto[],
        stackViews as ViewDto[],
        stackPages as PageDto[],
        stackAgents as Agent[],
        stackWorkflows as WorkflowDto[]
    )

    const isLoading =
        isLoadingObjects ||
        isLoadingNavigation ||
        isLoadingViews ||
        isLoadingPages ||
        isLoadingAgents ||
        isLoadingWorkflows

    return useDeepEqualsMemoValue({
        isLoading,
        template: {
            id: navigationApp.id,
            name: navigationApp.name,
            icon: navigationApp.icon,
            color_scheme: navigationApp.colorScheme,
            contents,
            version: 1,
        },
    })
}

export function getTemplateContentSections(
    stack: StackDto,
    role: RoleDto,
    objects: ObjectDto[],
    navigation: NavigationDto[],
    views: ViewDto[],
    pages: PageDto[],
    agents: Agent[],
    workflows: WorkflowDto[]
): AppTemplateContentSection[] {
    const objectSections = getObjectContentSections(objects)
    const viewsSections = getViewsContentSections(stack, role, navigation, views, pages)
    const agentsSections = getAgentsContentSections(agents)
    const workflowsSections = getWorkflowsContentSections(workflows)

    return [...objectSections, ...viewsSections, ...agentsSections, ...workflowsSections].filter(
        (s) => s.items.length > 0
    )
}

function getObjectContentSections(objects: ObjectDto[]): AppTemplateContentSection[] {
    return objects.reduce((acc, object) => {
        if (object.connection_options.is_disabled) return acc

        const section: AppTemplateContentSection = {
            label: `${object.name} fields`,
            items: getSectionItemsFromFields(object),
        }
        acc.push(section)

        return acc
    }, [] as AppTemplateContentSection[])
}

function getSectionItemsFromFields(object: ObjectDto): AppTemplateContentItem[] {
    const fields = object.fields.filter((f) => !f.connection_options?.is_disabled)

    const sortedFields = sortFieldsByOrder(fields, object)
    if (sortedFields.length < 1) return []

    return sortedFields.map(getSectionItemFromField)
}

function getSectionItemFromField(field: FieldDto): AppTemplateContentItem {
    const fieldIcon = getFieldIcon(field)
    const icon = getIconFromIconValue(fieldIcon)

    return {
        label: field.label,
        icon,
    }
}

function getViewsContentSections(
    stack: StackDto,
    role: RoleDto,
    navigation: NavigationDto[],
    views: ViewDto[],
    pages: PageDto[]
): AppTemplateContentSection[] {
    const navTree = buildNavTree(navigation, views, pages as PageDto[], role, stack._sid, false)
    const navItems = mapNavTreeToAppNavigationMenuItems(navTree)

    const items = navItems.reduce((acc, n) => {
        if (n.type === 'view') {
            acc.push({
                label: n.name,
                icon: n.icon,
            })
        }
        return acc
    }, [] as AppTemplateContentItem[])
    if (items.length < 1) return []

    return [
        {
            label: 'Views',
            items,
        },
    ]
}

function getAgentsContentSections(agents: Agent[]): AppTemplateContentSection[] {
    if (agents.length < 1) return []

    return [
        {
            label: 'Agents',
            items: agents.map((a) => ({
                label: a.title,
                icon: getIconFromIconValue(a.icon) || {
                    family: 'lucide',
                    name: 'Bot',
                },
            })),
        },
    ]
}

function getWorkflowsContentSections(workflows: WorkflowDto[]): AppTemplateContentSection[] {
    if (workflows.length < 1) return []

    return [
        {
            label: 'Automations',
            items: workflows.map((w) => ({
                label: w.name,
                icon: {
                    family: 'lucide',
                    name: 'Zap',
                },
            })),
        },
    ]
}
